import { Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import HtmlEditor from "pages/dashboard/CampaignDetails/distribution/emailDistribution/components/HtmlEditor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { cleanEmailHtml } from "shared/helpers";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import ButtonLoader from "shared/ui/ButtonLoader";
import InputField from "shared/ui/TextInput";
import { EDistributionMethods } from "store/enums/campaignDetails";
import { ERequestStatus } from "store/enums/index.enum";
import { ICampaignTranslation } from "store/interfaces/campaignTranslation";
import { selectCampaignInfo } from "store/slicers/campaignDetail";
import { UpdateDistributionMethodTexts } from "store/slicers/campaigns";

const DistributionTextTranslation = ({
  activeRow,
  campaignID,
  onAddSuccess,
}: {
  activeRow: ICampaignTranslation;
  campaignID: string;
  onAddSuccess: () => void;
}) => {
  const t = useTranslation();
  const data = useSelector(selectCampaignInfo);
  const dispatch = useAsyncDispatch();
  const [isLoading, setLoading] = useState(false);
  const [isEditorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);
  const methods = useForm({
    defaultValues: {
      SMSMessage: "",
      PushTitle: "",
      PushContent: "",
      EmailSubject: "",
      EmailBody: "",
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = async (formData) => {
    const { SMSMessage, PushTitle, PushContent, EmailSubject } = formData;
    setLoading(true);
    const EmailBody = editorRef?.current?.getContent();
    const htmlContent = cleanEmailHtml(EmailBody);
    const { meta } = await dispatch(
      UpdateDistributionMethodTexts({
        campaignID,
        languageID: activeRow.languageID,
        SMSMessage,
        PushTitle,
        PushContent,
        EmailBody: htmlContent,
        EmailSubject,
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      return;
    }
    setLoading(false);
    onAddSuccess();
  };

  const init = useCallback(() => {
    const actual_language_data = data.campaignTranslations.find(
      (i) => i.languageID === activeRow.languageID
    );

    // Check if editor is ready, if not, delay the content setting

    if (actual_language_data) {
      if (editorRef?.current && isEditorReady) {
        editorRef.current.setContent(actual_language_data.emailBody || "");
      }
      methods.reset({
        SMSMessage: actual_language_data.smsMessage,
        PushTitle: actual_language_data.pushTitle,
        PushContent: actual_language_data.pushContent,
        EmailSubject: actual_language_data.emailSubject,
        EmailBody: actual_language_data.emailBody || "",
      });
    }
  }, [
    activeRow?.languageID,
    data?.campaignTranslations,
    methods,
    isEditorReady,
  ]);

  const default_language_data = useMemo(() => {
    return data.campaignTranslations.find(
      (i) => i.languageID === data.defaultLanguageID
    );
  }, [data.campaignTranslations, data.defaultLanguageID]);

  const distributionMethodsData = useMemo(() => {
    const hasEmail = data.campaignTriggers.find(
      (i) => i.distributionMethod === EDistributionMethods.Email
    );
    const hasPush = data.campaignTriggers.find(
      (i) => i.distributionMethod === EDistributionMethods.Push
    );
    const hasSms = data.campaignTriggers.find(
      (i) => i.distributionMethod === EDistributionMethods.SMS
    );
    return {
      hasEmail,
      hasPush,
      hasSms,
    };
  }, [data?.campaignTriggers]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <Box>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          {distributionMethodsData?.hasPush && (
            <Grid item xs={12}>
              <Typography variant="h6">
                {t("dist_text_push_notif_title")}
              </Typography>
              <Grid container xs={12} my={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption">
                      {t("def_push_notif_title")}
                    </Typography>
                    <Typography variant="subtitle1">
                      {default_language_data?.pushTitle || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box ml={1}>
                    <InputField name="PushTitle" label="push_notif_title" />
                  </Box>
                </Grid>
              </Grid>
              <Grid container xs={12} my={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption">
                      {t("def_push_notif_content")}
                    </Typography>
                    <Typography variant="subtitle1">
                      {default_language_data?.pushContent || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box ml={1}>
                    <InputField name="PushContent" label="push_notif_content" />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}

          {distributionMethodsData?.hasSms && (
            <Grid item xs={12}>
              <Typography variant="h6">{t("dist_text_sms_title")}</Typography>
              <Grid container xs={12} my={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption">
                      {t("def_sms_message")}
                    </Typography>
                    <Typography variant="subtitle1">
                      {default_language_data?.smsMessage || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box ml={1}>
                    <InputField name="SMSMessage" label="sms_message" />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
          {distributionMethodsData?.hasEmail && (
            <Grid item xs={12}>
              <Typography variant="h6">{t("dist_text_email_title")}</Typography>
              <Grid container xs={12} my={2} spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption">
                      {t("def_email_subject")}
                    </Typography>
                    <Typography variant="subtitle1">
                      {default_language_data?.emailSubject || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box ml={1}>
                    <InputField name="EmailSubject" label="email_subject" />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption">
                      {t("def_email_body")}
                    </Typography>
                    <Typography variant="subtitle1">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: default_language_data?.emailBody || "-",
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box ml={1}>
                    <HtmlEditor
                      editorRef={editorRef}
                      onInitCb={(editor) => {
                        setEditorReady(true);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Box display="flex" justifyContent={"flex-end"} mt={2}>
          <ButtonLoader
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
          >
            <Typography>{t("save")}</Typography>
          </ButtonLoader>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default DistributionTextTranslation;
